import { Link } from 'react-router';

export function Terms() {
  return (
    <div className="min-h-screen bg-surface-0">
      {/* Header */}
      <header className="border-b border-[var(--border)] bg-[var(--surface-1)]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5 text-text-primary hover:text-text-secondary transition-colors">
            <img src="/logo.png" alt="Palaryn" width="22" height="22" />
            <span className="text-sm font-medium">Palaryn</span>
          </Link>
          <Link to="/privacy" className="text-sm text-text-tertiary hover:text-text-secondary transition-colors">
            Privacy Policy
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-4xl px-6 py-16">
        <h1 className="text-3xl font-bold text-text-primary">Terms of Service</h1>
        <p className="mt-2 text-sm text-text-tertiary">Last updated: February 24, 2026</p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-text-secondary">
          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">1. Agreement to Terms</h2>
            <p>
              By accessing or using the Palaryn platform ("Service"), operated by Palaryn ("we", "us", "our"),
              you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">2. Description of Service</h2>
            <p>
              Palaryn is a model-agnostic infrastructure layer that sits between AI agents and external services.
              It provides security and DLP enforcement, cost and budget controls, audit logging, and observability
              for AI agent tool calls. The Service is provided via API, SDK, forward proxy, and MCP bridge interfaces.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">3. Account Registration</h2>
            <p>
              To use the Service, you must create an account and provide accurate, complete information.
              You are responsible for maintaining the confidentiality of your account credentials, API keys,
              and all activity under your account. You must notify us immediately of any unauthorized use.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">4. Plans and Billing</h2>
            <p>
              The Service offers Free, Pro, Business, and Enterprise plans with varying usage limits and features.
              Paid plans are billed monthly via Stripe. By subscribing to a paid plan, you authorize us to charge
              your payment method on a recurring basis. You may cancel at any time; cancellation takes effect at
              the end of the current billing period. Refunds are not provided for partial months.
            </p>
            <p className="mt-2">
              Usage exceeding your plan's call limit will result in blocked tool executions until the next
              billing cycle or until you upgrade your plan.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="mt-2 list-disc list-inside space-y-1 text-text-secondary">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Attempt to bypass security controls, rate limits, or plan restrictions</li>
              <li>Transmit malware, viruses, or other harmful code through the gateway</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use the Service to facilitate unauthorized access to third-party systems</li>
              <li>Resell or redistribute the Service without written authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">6. Data and Privacy</h2>
            <p>
              Tool call data routed through Palaryn is processed according to your configured policies.
              DLP scanning detects and redacts secrets and PII as configured. Audit logs are retained per
              your plan's retention period. We do not sell your data. See our{' '}
              <Link to="/privacy" className="text-[var(--accent-teal)] hover:underline">Privacy Policy</Link>{' '}
              for details.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">7. API Keys and Security</h2>
            <p>
              You are solely responsible for the security of your API keys and access credentials.
              Do not share API keys or embed them in client-side code. We recommend using workspace-level
              keys with appropriate RBAC roles and rotating keys regularly.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">8. Service Availability</h2>
            <p>
              We strive to maintain high availability but do not guarantee uninterrupted service.
              We may perform maintenance with reasonable notice. The Service is provided "as is"
              without warranties of any kind, express or implied.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Palaryn shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, or any loss of profits or data,
              arising from your use of the Service. Our total liability shall not exceed the amount
              you paid us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">10. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time for violations of
              these Terms. You may terminate your account at any time by contacting us. Upon termination,
              your data will be deleted in accordance with our retention policies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">11. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of material changes via
              email or through the Service. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-text-primary mb-3">12. Contact</h2>
            <p>
              For questions about these Terms, contact us at{' '}
              <a href="mailto:legal@palaryn.com" className="text-[var(--accent-teal)] hover:underline">
                legal@palaryn.com
              </a>.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
